import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Globe, DollarSign, Archive, Package, Receipt, FileText, CheckSquare, Clock, ShieldCheck, Users, Loader2, Activity } from "lucide-react";
import axios from "axios";

const PHP_BASE_URL = "https://bluebilll.sbe-idn.com";
const API_URL = "/api";

const PortalPage = ({ setAuth }) => {
    const navigate = useNavigate();
    const [userData, setUserData] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [loadingModule, setLoadingModule] = useState(null);

    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            setUserData(JSON.parse(storedUser));
        } else {
            navigate('/login');
        }
    }, [navigate]);

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        if (setAuth) setAuth(false);
        navigate('/login');
    };

    const checkModuleAccess = (requiredPermission) => {
        if (!userData) return false;
        if (userData.role === "ADMIN") return true;
        if (!requiredPermission) return true;

        let perms = [];
        try { perms = JSON.parse(userData.permissions || "[]"); } 
        catch(e) { perms = Array.isArray(userData.permissions) ? userData.permissions : []; }
        
        return perms.includes(requiredPermission);
    };

    // Daftar Modul
    const allModules = [
        { 
            id: "cc-vn", name: "Customs Clearance VN", icon: Globe, 
            isActive: true, reqPerm: "Modul Customs Clearance",
            phpPath: "/app/customs_clearance/index.php?type=vn"
        },
        { 
            id: "cc-non-vn", name: "Customs Clearance Non-VN", icon: Globe, 
            isActive: true, reqPerm: "Modul Customs Clearance",
            phpPath: "/app/customs_clearance/index.php?type=non-vn"
        },
        { 
            id: "delivery", name: "Delivery Charge", icon: DollarSign, 
            isActive: true, reqPerm: "Modul Delivery",
            phpPath: "/app/delivery_charge/index.php"
        },
        { 
            id: "storage", name: "Storage", icon: Archive, 
            isActive: true, reqPerm: "Modul Storage",
            phpPath: "/app/storage/index.php"
        },
        { 
            id: "cargo", name: "Cargo", icon: Package, 
            isActive: true, reqPerm: "Modul Cargo",
            phpPath: "/app/invoice_cargo/index.php"
        },
        { 
            id: "debit-note", name: "Debit Note / Collection Fee", icon: Receipt, 
            isActive: true, reqPerm: "Modul Debit Note",
            phpPath: "/app/debit_note_collection_fee/index.php"
        },
        { 
            id: "ddp-vn", name: "Delivered Duty Paid VN", icon: CheckSquare, 
            isActive: true, reqPerm: "Modul DDP VN",
            phpPath: "/app/delivered_duty_paid/index.php"
        },
        { 
            id: "import-cust", name: "Import Customer (XLSX)", icon: FileText, 
            isActive: true, reqPerm: "Modul Import Customer",
            phpPath: null,
            path: "/invoice-c/customers"
        },
        { 
            id: "invoice-a", name: "Invoice A (Billing)", icon: FileText, 
            isActive: true, reqPerm: "Modul Invoice A",
            phpPath: "/app/dutytax_handling_charge/invoice_a.php"
        },
        { 
            id: "invoice-c", name: "Invoice C (Billing)", icon: FileText, 
            path: "/invoice-c/dashboard", isActive: true, reqPerm: "Modul Invoice C",
            phpPath: null
        },
        {
            id: "duty-tax", name: "Report Clearance", icon: FileText,
            path: "/duty-tax", isActive: true, reqPerm: "Modul Report Clearance",
            phpPath: null
        },
        { 
            id: "storage-myob", name: "Storage MYOB", icon: Archive, 
            isActive: true, reqPerm: "Modul Storage MYOB",
            phpPath: "/app/storage_myob/index.php"
        },
        { 
            id: "overseas-billing", name: "Overseas Billing", icon: Archive, 
            isActive: true, reqPerm: "Modul Overseas Billing",
            phpPath: "/app/overseas_billing/index.php"
        },
        { 
            id: "local-courier", name: "Local Courier", icon: Archive, 
            isActive: true, reqPerm: "Modul Local Courier",
            phpPath: "/app/local_courier/index.php"
        },
        { 
            id: "ntpn", name: "NTPN", icon: ShieldCheck, 
            isActive: true, reqPerm: "Modul NTPN",
            phpPath: "/app/ntpn/upload.php"
        },
        { 
            id: "history", name: "History", icon: Clock, 
            isActive: true, reqPerm: "Modul History",
            phpPath: "/app/history/index.php"
        },
        { 
            id: "billing-report", name: "Billing Report", icon: FileText,
            path: "/billing-report", isActive: true, reqPerm: "Modul History",
        },
        {
            id: "faktur", name: "Faktur Pajak", icon: FileText, 
            isActive: true, reqPerm: "Modul Faktur Pajak",
            phpPath: "/app/faktur-pajak/index.php"
        },
    ];

    const filteredModules = allModules
        .filter(m => checkModuleAccess(m.reqPerm))
        .filter(m => m.name.toLowerCase().includes(searchQuery.toLowerCase()));

    // Fungsi buka modul PHP via SSO
    const handleOpenPHPModule = async (module) => {
        setLoadingModule(module.id);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.post(`${API_URL}/auth/sso-token`, { module_name: module.name }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const ssoToken = res.data.sso_token;
            const redirectUrl = `${PHP_BASE_URL}/sso.php?sso_token=${ssoToken}&redirect=${encodeURIComponent(module.phpPath)}`;
            window.open(redirectUrl, '_blank');
        } catch (error) {
            alert('Gagal membuka modul. Silakan coba lagi.');
        } finally {
            setLoadingModule(null);
        }
    };

    const handleOpenModule = async (module, hasAccess) => {
        if (!hasAccess) {
            alert(`Akses Ditolak! Anda tidak memiliki izin untuk membuka modul "${module.name}".`);
            return;
        }
        if (!module.isActive) {
            alert(`Modul "${module.name}" sedang dalam tahap pengembangan.`);
            return;
        }
        // Kalau ada path internal React
        if (module.path) {
            navigate(module.path);
            return;
        }
        // Kalau modul PHP
        if (module.phpPath) {
            await handleOpenPHPModule(module);
            return;
        }
        alert(`Modul "${module.name}" sedang dalam tahap pengembangan.`);
    };

    return (
        <div className="min-h-screen bg-slate-50 p-6 md:p-10">
            <div className="max-w-6xl mx-auto space-y-8">
                
                {/* Header Portal */}
                <div className="flex justify-between items-center bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
                    <div className="flex items-center gap-4">
                        <img src="/logo.png" alt="OCS Logo" className="h-12 object-contain hidden md:block" />
                        <div>
                            <h1 className="text-xl font-black text-slate-800">PT. Sayap Biru Ekspres</h1>
                            <p className="text-slate-500 font-medium">Halo, {userData?.name || 'Karyawan'}</p>
                        </div>
                    </div>
                    <button onClick={handleLogout} className="text-red-500 border border-red-200 hover:bg-red-50 px-6 py-2.5 rounded-lg font-bold transition">
                        Logout
                    </button>
                </div>

                {/* Area Pencarian */}
                <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                    <h2 className="text-slate-600 font-medium w-full md:w-auto">Akses modul Anda:</h2>
                    <input 
                        type="text" 
                        placeholder="Cari modul..." 
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full md:w-96 p-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
                    />
                </div>

                {/* Grid Modul */}
                {filteredModules.length === 0 ? (
                    <div className="text-center py-16 text-slate-400">
                        <p className="font-medium">Tidak ada modul yang tersedia untuk akun Anda.</p>
                        <p className="text-sm mt-1">Hubungi admin untuk mendapatkan akses modul.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {filteredModules.map((mod) => {
                            const Icon = mod.icon;
                            const isLoading = loadingModule === mod.id;
                            
                            return (
                                <div key={mod.id} className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md flex flex-col items-center text-center transition">
                                    <div className="flex items-center gap-3 w-full mb-6">
                                        <Icon size={24} className="text-blue-600" />
                                        <h3 className="font-bold text-sm text-left leading-tight text-slate-800">{mod.name}</h3>
                                    </div>
                                    <button 
                                        onClick={() => handleOpenModule(mod, true)}
                                        disabled={isLoading}
                                        className={`w-full py-2.5 rounded-lg font-bold transition flex justify-center items-center gap-2 
                                            ${mod.isActive ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-md' : 
                                              'bg-slate-100 text-slate-400 cursor-not-allowed'}`}
                                    >
                                        {isLoading ? (
                                            <><Loader2 size={16} className="animate-spin"/> Membuka...</>
                                        ) : (
                                            'Buka'
                                        )}
                                    </button>
                                </div>
                            )
                        })}
                    </div>
                )}

                {/* Area Admin Khusus */}
                {userData?.role === "ADMIN" && (
                    <div className="mt-12 pt-8 border-t border-slate-200 flex justify-between items-center">
                        <h3 className="font-bold text-slate-800 text-lg">Admin</h3>
                        <button onClick={() => navigate('/users')} className="text-blue-600 border border-blue-200 hover:bg-blue-50 px-6 py-2.5 rounded-lg font-bold transition flex items-center gap-2">
                            <Users size={18} /> Kelola User
                        </button>
                        <button onClick={() => navigate("/activity-log")} className="text-slate-700 border border-slate-200 hover:bg-slate-50 px-6 py-2.5 rounded-lg font-bold transition flex items-center gap-2">
                            <Activity size={18} /> Activity Log
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PortalPage;